import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { Layout } from '../../components/Layout';
import { checkIn, checkOut, completeProject, deletePhoto, getProject, getSketchUploadUrl, getUploadUrl, listPhotos, recordPhoto, } from '../projects/api';
import { getEstimateByProjectId, listPhotos as listEstimatePhotos } from '../estimates/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SketchCanvas } from '../estimates/SketchCanvas';
export const FieldJobView = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [instructionsOpen, setInstructionsOpen] = useState(false);
    const [geoError, setGeoError] = useState(null);
    const [checkInLoading, setCheckInLoading] = useState(false);
    const [photoLoading, setPhotoLoading] = useState(false);
    const [photoError, setPhotoError] = useState(null);
    const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
    const [completionNotes, setCompletionNotes] = useState('');
    const [completing, setCompleting] = useState(false);
    const beforeFileRef = useRef(null);
    const afterFileRef = useRef(null);
    const [sketchSaving, setSketchSaving] = useState(false);
    const [sketchError, setSketchError] = useState(null);
    const [sketchSaved, setSketchSaved] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const loadProject = useCallback(async () => {
        if (!projectId)
            return;
        setLoading(true);
        setError(null);
        try {
            const [p, photos] = await Promise.all([getProject(projectId), listPhotos(projectId)]);
            // Merge signed URLs from listPhotos into the project's photo array
            const urlMap = new Map(photos.map((ph) => [ph.photoId, ph.url]));
            p.photos = p.photos.map((ph) => ({ ...ph, url: urlMap.get(ph.photoId) ?? ph.url }));
            // If this project came from an estimate, also load the estimate's photos.
            // Deduplicate by photoId (new conversions will have them already copied in).
            // If sourceEstimateId is missing (old project converted before that field was added),
            // fall back to a reverse lookup using convertedToProjectId.
            let estimateId = p.sourceEstimateId;
            if (!estimateId) {
                try {
                    const sourceEstimate = await getEstimateByProjectId(p.id);
                    estimateId = sourceEstimate?.id;
                }
                catch {
                    // Non-fatal
                }
            }
            if (estimateId) {
                try {
                    const estimatePhotos = await listEstimatePhotos(estimateId);
                    const existingIds = new Set(p.photos.map((ph) => ph.photoId));
                    const merged = estimatePhotos
                        .filter((ep) => !existingIds.has(ep.photoId))
                        .map((ep) => ({
                        photoId: ep.photoId,
                        type: ep.uploadedBy === 'sketch' ? 'sketch' : 'before',
                        s3Key: ep.s3Key,
                        url: ep.url,
                        uploadedAt: ep.uploadedAt,
                        uploadedBy: ep.uploadedBy,
                    }));
                    p.photos = [...p.photos, ...merged];
                }
                catch {
                    // Non-fatal — estimate photos are supplemental
                }
            }
            setProject(p);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load job');
        }
        finally {
            setLoading(false);
        }
    }, [projectId]);
    useEffect(() => {
        loadProject();
    }, [loadProject]);
    const lastCheckIn = project && project.checkIns.length > 0
        ? project.checkIns[project.checkIns.length - 1]
        : undefined;
    const isCheckedIn = lastCheckIn?.type === 'checkin';
    const handleCheckIn = () => {
        if (!projectId)
            return;
        setGeoError(null);
        setCheckInLoading(true);
        navigator.geolocation.getCurrentPosition(async (pos) => {
            try {
                const updated = await checkIn(projectId, pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
                setProject(updated);
            }
            catch (e) {
                setGeoError(e.message ?? 'Check-in failed');
            }
            finally {
                setCheckInLoading(false);
            }
        }, (err) => {
            setCheckInLoading(false);
            if (err.code === err.PERMISSION_DENIED) {
                setGeoError('Location access denied. Please enable location permissions and try again.');
            }
            else {
                setGeoError('Unable to get your location. Please try again.');
            }
        }, { enableHighAccuracy: true, timeout: 10000 });
    };
    const handleCheckOut = async () => {
        if (!projectId)
            return;
        setGeoError(null);
        setCheckInLoading(true);
        try {
            const updated = await checkOut(projectId);
            setProject(updated);
        }
        catch (e) {
            setGeoError(e.message ?? 'Check-out failed');
        }
        finally {
            setCheckInLoading(false);
        }
    };
    const handlePhotoUpload = async (files, type) => {
        if (!projectId || files.length === 0)
            return;
        setPhotoLoading(true);
        setPhotoError(null);
        try {
            await Promise.all(Array.from(files).map(async (file) => {
                const { photoId, s3Key, uploadUrl } = await getUploadUrl(projectId, type, file.name, file.type);
                await fetch(uploadUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type },
                });
                await recordPhoto(projectId, photoId, type, s3Key, user?.username ?? 'unknown');
            }));
            await loadProject();
        }
        catch (e) {
            setPhotoError(e.message ?? 'Failed to upload photo');
        }
        finally {
            setPhotoLoading(false);
        }
    };
    const handleDeletePhoto = async (photoId) => {
        if (!projectId)
            return;
        try {
            await deletePhoto(projectId, photoId);
            await loadProject();
        }
        catch (e) {
            setPhotoError(e.message ?? 'Failed to delete photo');
        }
    };
    const handleSketchSave = async (blob) => {
        if (!projectId)
            return;
        setSketchSaving(true);
        setSketchError(null);
        setSketchSaved(false);
        try {
            const { uploadUrl, s3Key } = await getSketchUploadUrl(projectId);
            await fetch(uploadUrl, {
                method: 'PUT',
                body: blob,
                headers: { 'Content-Type': 'image/png' },
            });
            const sketchId = s3Key.split('/').pop()?.replace('.png', '') ?? crypto.randomUUID();
            await recordPhoto(projectId, sketchId, 'sketch', s3Key, user?.username ?? 'unknown');
            await loadProject();
            setSketchSaved(true);
            setTimeout(() => setSketchSaved(false), 3000);
        }
        catch (e) {
            setSketchError(e.message ?? 'Failed to save sketch');
        }
        finally {
            setSketchSaving(false);
        }
    };
    const handleComplete = async () => {
        if (!projectId)
            return;
        setCompleting(true);
        try {
            const updated = await completeProject(projectId, completionNotes || undefined);
            setProject(updated);
            setShowCompleteConfirm(false);
            setCompletionNotes('');
        }
        catch (e) {
            setError(e.message ?? 'Failed to complete job');
        }
        finally {
            setCompleting(false);
        }
    };
    const addressString = project
        ? `${project.siteAddress.street}, ${project.siteAddress.city}, ${project.siteAddress.state} ${project.siteAddress.zip}`
        : '';
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) }) }));
    }
    if (error && !project) {
        return (_jsxs(Layout, { children: [_jsx("div", { style: styles.errorBanner, children: error }), _jsx("button", { style: styles.backLink, onClick: () => navigate(-1), children: "\u2190 Back" })] }));
    }
    if (!project)
        return null;
    const beforePhotos = project.photos.filter((p) => p.type === 'before');
    const afterPhotos = project.photos.filter((p) => p.type === 'after');
    const sketchPhotos = project.photos.filter((p) => p.type === 'sketch');
    const isActive = project.status === 'assigned' || project.status === 'in_progress';
    const thumbSize = isMobile ? 100 : 80;
    return (_jsxs(Layout, { children: [_jsxs("div", { style: { ...styles.container, padding: isMobile ? '0' : undefined }, children: [_jsxs("div", { style: styles.card, children: [_jsx("button", { style: styles.backLink, onClick: () => navigate(-1), children: "\u2190 Back" }), _jsx("h1", { style: styles.jobTitle, children: project.title }), _jsx("p", { style: styles.customerName, children: project.customerName }), _jsx("a", { href: `https://maps.google.com/?q=${encodeURIComponent(addressString)}`, target: "_blank", rel: "noopener noreferrer", style: styles.addressLink, children: addressString }), _jsxs("div", { style: styles.badgesRow, children: [_jsx("span", { style: statusBadgeStyle(project.status), children: statusLabel(project.status) }), project.priority === 'urgent' && (_jsx("span", { style: styles.badgeOrange, children: "Urgent" }))] })] }), _jsxs("div", { style: styles.card, children: [_jsxs("button", { style: styles.toggleButton, onClick: () => setInstructionsOpen((prev) => !prev), children: [instructionsOpen ? 'Hide Instructions' : 'Show Instructions', _jsx("span", { style: { marginLeft: '8px' }, children: instructionsOpen ? '▲' : '▼' })] }), instructionsOpen && (_jsx("p", { style: styles.instructionsText, children: project.instructions || 'No instructions provided.' }))] }), isActive && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Check In / Check Out" }), lastCheckIn && (_jsxs("p", { style: styles.checkInStatus, children: ["Last:", ' ', _jsx("strong", { children: lastCheckIn.type === 'checkin' ? 'Checked In' : 'Checked Out' }), ' — ', new Date(lastCheckIn.timestamp).toLocaleString()] })), geoError && _jsx("div", { style: styles.errorBanner, children: geoError }), !isCheckedIn ? (_jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonGreen }, onClick: handleCheckIn, disabled: checkInLoading, children: checkInLoading ? 'Getting Location…' : 'Check In' })) : (_jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonOrange }, onClick: handleCheckOut, disabled: checkInLoading, children: checkInLoading ? 'Checking Out…' : 'Check Out' }))] })), (beforePhotos.length > 0 || afterPhotos.length > 0 || isActive) && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Photos" }), photoError && _jsx("div", { style: styles.errorBanner, children: photoError }), photoLoading && (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }, children: [_jsx("div", { style: styles.spinnerSmall }), _jsx("span", { style: { fontSize: '14px', color: '#86868b' }, children: "Uploading\u2026" })] })), _jsxs("div", { style: styles.photoSection, children: [_jsxs("p", { style: styles.photoSectionLabel, children: ["Before Photos", ' ', _jsxs("span", { style: styles.photoCount, children: ["(", beforePhotos.length, ")"] })] }), _jsx("div", { style: styles.thumbnailsRow, children: beforePhotos.map((photo) => (_jsxs("div", { style: { ...styles.thumbWrapper, width: thumbSize, height: thumbSize }, children: [_jsx("img", { src: photo.url, alt: "Before", style: { ...styles.thumbnail, width: thumbSize, height: thumbSize, cursor: 'pointer' }, onClick: () => setLightboxUrl(photo.url ?? null) }), isActive && (_jsx("button", { style: {
                                                        ...styles.thumbDeleteBtn,
                                                        ...(isMobile
                                                            ? { width: '32px', height: '32px', fontSize: '16px' }
                                                            : {}),
                                                    }, onClick: () => handleDeletePhoto(photo.photoId), children: "\u00D7" }))] }, photo.photoId))) }), isActive && (_jsxs(_Fragment, { children: [_jsx("input", { ref: beforeFileRef, type: "file", accept: "image/*", multiple: true, style: { display: 'none' }, onChange: (e) => {
                                                    if (e.target.files?.length)
                                                        handlePhotoUpload(e.target.files, 'before');
                                                    e.target.value = '';
                                                } }), _jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonSecondary }, onClick: () => beforeFileRef.current?.click(), disabled: photoLoading, children: "Take / Upload Before Photos" })] }))] }), _jsxs("div", { style: { ...styles.photoSection, marginTop: '20px' }, children: [_jsxs("p", { style: styles.photoSectionLabel, children: ["After Photos", ' ', _jsxs("span", { style: styles.photoCount, children: ["(", afterPhotos.length, ")"] })] }), _jsx("div", { style: styles.thumbnailsRow, children: afterPhotos.map((photo) => (_jsxs("div", { style: { ...styles.thumbWrapper, width: thumbSize, height: thumbSize }, children: [_jsx("img", { src: photo.url, alt: "After", style: { ...styles.thumbnail, width: thumbSize, height: thumbSize, cursor: 'pointer' }, onClick: () => setLightboxUrl(photo.url ?? null) }), isActive && (_jsx("button", { style: {
                                                        ...styles.thumbDeleteBtn,
                                                        ...(isMobile
                                                            ? { width: '32px', height: '32px', fontSize: '16px' }
                                                            : {}),
                                                    }, onClick: () => handleDeletePhoto(photo.photoId), children: "\u00D7" }))] }, photo.photoId))) }), isActive && (_jsxs(_Fragment, { children: [_jsx("input", { ref: afterFileRef, type: "file", accept: "image/*", multiple: true, style: { display: 'none' }, onChange: (e) => {
                                                    if (e.target.files?.length)
                                                        handlePhotoUpload(e.target.files, 'after');
                                                    e.target.value = '';
                                                } }), _jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonSecondary }, onClick: () => afterFileRef.current?.click(), disabled: photoLoading, children: "Take / Upload After Photos" })] }))] })] })), (isActive || sketchPhotos.length > 0) && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Sketch" }), isActive && (_jsxs(_Fragment, { children: [sketchError && _jsx("div", { style: styles.errorBanner, children: sketchError }), _jsx(SketchCanvas, { onSave: handleSketchSave }), sketchSaving && (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }, children: [_jsx("div", { style: styles.spinnerSmall }), _jsx("span", { style: { fontSize: '14px', color: '#86868b' }, children: "Saving sketch\u2026" })] })), sketchSaved && !sketchSaving && (_jsx("p", { style: { fontSize: '14px', color: '#248a3d', marginTop: '10px' }, children: "Sketch saved!" }))] })), sketchPhotos.length > 0 && (_jsxs("div", { style: { marginTop: isActive ? '20px' : '0' }, children: [_jsxs("p", { style: styles.photoSectionLabel, children: ["Saved Sketches", ' ', _jsxs("span", { style: styles.photoCount, children: ["(", sketchPhotos.length, ")"] })] }), _jsx("div", { style: styles.thumbnailsRow, children: sketchPhotos
                                            .slice()
                                            .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))
                                            .map((photo) => (_jsx("div", { style: { ...styles.thumbWrapper, width: thumbSize, height: thumbSize }, children: _jsx("img", { src: photo.url, alt: "Sketch", style: { ...styles.thumbnail, width: thumbSize, height: thumbSize, cursor: 'pointer' }, onClick: () => setLightboxUrl(photo.url ?? null) }) }, photo.photoId))) })] }))] })), project.status === 'in_progress' && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Complete Job" }), !showCompleteConfirm ? (_jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonGreen }, onClick: () => setShowCompleteConfirm(true), children: "Mark Job Complete" })) : (_jsxs("div", { children: [_jsx("textarea", { style: styles.textarea, rows: 4, placeholder: "Optional completion notes\u2026", value: completionNotes, onChange: (e) => setCompletionNotes(e.target.value) }), _jsxs("div", { style: { display: 'flex', gap: '12px', marginTop: '12px' }, children: [_jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonGray, flex: 1 }, onClick: () => {
                                                    setShowCompleteConfirm(false);
                                                    setCompletionNotes('');
                                                }, disabled: completing, children: "Cancel" }), _jsx("button", { style: { ...styles.bigButton, ...styles.bigButtonGreen, flex: 2 }, onClick: handleComplete, disabled: completing, children: completing ? 'Completing…' : 'Confirm Complete' })] })] }))] })), project.status === 'completed' && (_jsxs("div", { style: styles.card, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: project.completionNotes ? '20px' : '0' }, children: [_jsx("div", { style: { ...styles.completedCheck, fontSize: '36px', marginBottom: 0 }, children: "\u2713" }), _jsxs("div", { children: [_jsx("h2", { style: { ...styles.completedTitle, fontSize: '22px', margin: 0 }, children: "Job Completed" }), project.completedAt && (_jsx("p", { style: { ...styles.completedDate, margin: '4px 0 0 0' }, children: new Date(project.completedAt).toLocaleString() }))] })] }), project.completionNotes && (_jsxs("div", { children: [_jsx("p", { style: { fontSize: '13px', fontWeight: '700', color: '#86868b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }, children: "Installer Notes" }), _jsx("p", { style: { ...styles.completionNotes, backgroundColor: 'rgba(0,0,0,0.03)', padding: '12px 14px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.07)' }, children: project.completionNotes })] }))] })), error && _jsx("div", { style: styles.errorBanner, children: error })] }), lightboxUrl && (_jsxs("div", { style: {
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }, onClick: () => setLightboxUrl(null), children: [_jsx("img", { src: lightboxUrl, alt: "Full size", style: {
                            maxWidth: '95vw',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: '8px',
                        }, onClick: (e) => e.stopPropagation() }), _jsx("button", { onClick: () => setLightboxUrl(null), style: {
                            position: 'fixed',
                            top: '20px',
                            right: '20px',
                            width: '44px',
                            height: '44px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '22px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }, children: "\u00D7" })] }))] }));
};
function statusLabel(status) {
    switch (status) {
        case 'draft': return 'Draft';
        case 'assigned': return 'Assigned';
        case 'in_progress': return 'In Progress';
        case 'completed': return 'Completed';
        default: return status;
    }
}
function statusBadgeStyle(status) {
    switch (status) {
        case 'draft': return styles.badgeGray;
        case 'assigned': return styles.badgeBlue;
        case 'in_progress': return styles.badgeOrange;
        case 'completed': return styles.badgeGreen;
        default: return styles.badgeGray;
    }
}
const styles = {
    container: {
        maxWidth: '600px',
        margin: '0 auto',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '80px 0',
    },
    spinner: {
        width: '36px',
        height: '36px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    spinnerSmall: {
        width: '18px',
        height: '18px',
        border: '2px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 16px 0',
        display: 'block',
    },
    card: {
        padding: '24px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '16px',
    },
    jobTitle: {
        fontSize: '28px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.4px',
        lineHeight: '1.2',
    },
    customerName: {
        fontSize: '17px',
        fontWeight: '500',
        color: '#3c3c43',
        margin: '0 0 8px 0',
    },
    addressLink: {
        display: 'block',
        fontSize: '15px',
        color: '#0071e3',
        textDecoration: 'none',
        margin: '0 0 12px 0',
        wordBreak: 'break-word',
    },
    badgesRow: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    toggleButton: {
        width: '100%',
        padding: '14px 18px',
        backgroundColor: 'rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#1d1d1f',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    instructionsText: {
        marginTop: '16px',
        marginBottom: 0,
        fontSize: '15px',
        color: '#1d1d1f',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
    },
    sectionTitle: {
        fontSize: '19px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 16px 0',
        letterSpacing: '-0.3px',
    },
    checkInStatus: {
        fontSize: '15px',
        color: '#3c3c43',
        margin: '0 0 14px 0',
    },
    bigButton: {
        width: '100%',
        minHeight: '52px',
        padding: '14px 20px',
        border: 'none',
        borderRadius: '14px',
        fontSize: '17px',
        fontWeight: '700',
        cursor: 'pointer',
        letterSpacing: '-0.2px',
    },
    bigButtonGreen: {
        backgroundColor: '#34c759',
        color: '#fff',
    },
    bigButtonOrange: {
        backgroundColor: '#ff9500',
        color: '#fff',
    },
    bigButtonSecondary: {
        backgroundColor: 'rgba(0,113,227,0.08)',
        color: '#0071e3',
        border: '1px solid rgba(0,113,227,0.25)',
    },
    bigButtonGray: {
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
    },
    photoSection: {},
    photoSectionLabel: {
        fontSize: '15px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 10px 0',
    },
    photoCount: {
        fontWeight: '400',
        color: '#86868b',
    },
    thumbnailsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
    },
    thumbWrapper: {
        position: 'relative',
        width: '80px',
        height: '80px',
    },
    thumbnail: {
        width: '80px',
        height: '80px',
        objectFit: 'cover',
        borderRadius: '10px',
        display: 'block',
    },
    thumbDeleteBtn: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,59,48,0.85)',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '700',
        lineHeight: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    textarea: {
        width: '100%',
        padding: '12px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    completedCheck: {
        fontSize: '64px',
        color: '#34c759',
        marginBottom: '8px',
        lineHeight: '1',
    },
    completedTitle: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 8px 0',
    },
    completedDate: {
        fontSize: '15px',
        color: '#86868b',
        margin: '0 0 12px 0',
    },
    completionNotes: {
        fontSize: '15px',
        color: '#3c3c43',
        margin: 0,
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5',
    },
    errorBanner: {
        padding: '14px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '12px',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeOrange: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(255,149,0,0.15)',
        color: '#c45c00',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
};
//# sourceMappingURL=FieldJobView.js.map